// v6 — added "traded/swapped" as third outcome option in return to service modal
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { getPhotoUrl, uploadPhoto } from '../lib/storage';
import { useAuth } from '../components/AuthContext';
import MobileTagout from './MobileTagout';
import InlineMessage from '../components/InlineMessage';
import useMessage from '../lib/useMessage';

const STATUS_BADGE = { tagged_out: 'badge-tagged', in_repair: 'badge-repair', in_transit: 'badge-transit', Returned: 'badge-approved' };
const STATUS_LABEL = { tagged_out: 'Tagged out', in_repair: 'In repair', in_transit: 'In transit', Returned: 'Returned' };

function fmt$(v) { return v != null ? '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'; }

async function logHistory(toolId, eventType, oldStatus, newStatus, changedByName, changedById, notes, metadata) {
  await supabase.from('tool_history').insert({
    tool_id: toolId, event_type: eventType,
    old_status: oldStatus || null, new_status: newStatus || null,
    changed_by_name: changedByName || null, changed_by_id: changedById || null,
    notes: notes || null, metadata: metadata || null,
  });
}

// ── MOBILE TOOL PICKER ─────────────────────────────────────
function MobileToolPicker({ profile, isAdmin, onSelect, onClose }) {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    async function loadTools() {
      let q = supabase
        .from('tools')
        .select('id, name, model_number, serial_number, status, branch_id, department, station_truck, branches(name)')
        .eq('status', 'active')
        .order('name');
      if (!isAdmin && profile?.branch_id) q = q.eq('branch_id', profile.branch_id);
      if (profile?.department) q = q.eq('department', profile.department);
      if (profile?.station_truck) q = q.eq('station_truck', profile.station_truck);
      const { data } = await q;
      setTools(data || []);
      setLoading(false);
    }
    loadTools();
  }, [isAdmin, profile]);

  const filtered = tools.filter(t => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [t.name, t.model_number, t.serial_number, t.station_truck].filter(Boolean).join(' ').toLowerCase().includes(q);
  });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'white', zIndex: 2000, display: 'flex', flexDirection: 'column' }}>
      <div style={{ background: '#C62828', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12, position: 'sticky', top: 0, zIndex: 10 }}>
        <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8, color: 'white', padding: '6px 10px', fontSize: 18, cursor: 'pointer' }}>←</button>
        <div>
          <div style={{ color: 'white', fontWeight: 700, fontSize: 17 }}>Tag out tool</div>
          <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>Select a tool to tag out</div>
        </div>
      </div>
      <div style={{ padding: '14px 20px', borderBottom: '1px solid #E8E8E8', background: 'white', position: 'sticky', top: 60, zIndex: 9 }}>
        <input
          autoFocus type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search name, model, serial..."
          style={{ width: '100%', padding: '10px 14px', fontSize: 15, border: '2px solid #E0E0E0', borderRadius: 10, fontFamily: 'Barlow, sans-serif', background: '#F8F8F8', boxSizing: 'border-box' }}
          onFocus={e => e.target.style.borderColor = '#C62828'}
          onBlur={e => e.target.style.borderColor = '#E0E0E0'}
        />
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#9A9A9A' }}>Loading tools...</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#9A9A9A' }}>No active tools found.</div>
        ) : filtered.map(tool => (
          <button key={tool.id} onClick={() => onSelect(tool)} style={{ width: '100%', padding: '16px 20px', borderBottom: '1px solid #F0F0F0', background: 'white', border: 'none', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: '#FEE8E8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 18 }}>🔧</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#0F0F0F' }}>{tool.name}</div>
              <div style={{ fontSize: 12, color: '#5C5C5C', marginTop: 2 }}>
                {[tool.model_number, tool.serial_number ? 'S/N: ' + tool.serial_number : null, tool.station_truck ? 'Station ' + tool.station_truck : null].filter(Boolean).join(' · ')}
              </div>
            </div>
            <span style={{ color: '#D0D0D0', fontSize: 18, flexShrink: 0 }}>→</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── TAGOUT PAGE ────────────────────────────────────────────
export default function TagoutPage() {
  const { profile, isAdmin, isManager, isExecutive, isTeamMember } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [showReturn, setShowReturn] = useState(false);
  const [returnLog, setReturnLog] = useState(null);
  const [returnForm, setReturnForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [photoUrls, setPhotoUrls] = useState({});

  const returnMsg = useMessage();

  const isMobile = window.innerWidth <= 768;
  const [showToolPicker, setShowToolPicker] = useState(false);
  const [showMobileTagout, setShowMobileTagout] = useState(false);
  const [selectedTool, setSelectedTool] = useState(null);

  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [receiptUploading, setReceiptUploading] = useState(false);
  const receiptInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    let q = supabase
      .from('tagout_log')
      .select('*, tools!inner(id, name, model_number, serial_number, branch_id, department, branches(name), tool_models(msrp))')
      .order('tag_date', { ascending: false });

    if (isManager && !isAdmin && !isExecutive && profile?.branch_id) {
      q = q.eq('tools.branch_id', profile.branch_id);
      if (profile?.department) q = q.eq('tools.department', profile.department);
    }
    if (isTeamMember && profile?.branch_id) {
      q = q.eq('tools.branch_id', profile.branch_id);
      if (profile?.department) q = q.eq('tools.department', profile.department);
    }

    const { data } = await q;
    const logs = data || [];
    setLogs(logs);

    const urlMap = {};
    logs.filter(r => r.photo_url).forEach(r => {
      const url = getPhotoUrl(r.photo_url);
      if (url) urlMap[r.id] = url;
    });
    setPhotoUrls(urlMap);
    setLoading(false);
  }, [isManager, isAdmin, isExecutive, isTeamMember, profile?.branch_id, profile?.department]);

  useEffect(() => { load(); }, [load]);

  const filtered = logs.filter(r => {
    const q = search.toLowerCase();
    const match = !q || [r.tools?.name, r.reason, r.tagged_by_name, r.issue_description, r.shop_name].filter(Boolean).join(' ').toLowerCase().includes(q);
    const sf = !filter || r.status === filter;
    return match && sf;
  });

  const stats = {
    tagged: logs.filter(r => r.status === 'tagged_out').length,
    repair: logs.filter(r => r.status === 'in_repair').length,
    transit: logs.filter(r => r.status === 'in_transit').length,
    returned: logs.filter(r => r.status === 'Returned').length,
  };

  function openReturn(log) {
    setReturnLog(log);
    setReturnForm({
      returned_by_name: profile?.full_name || '',
      resolution_notes: '',
      actual_repair_cost: log.estimated_repair_cost || '',
      repair_or_replace: 'repaired',
      new_status: 'active',
      replacement_msrp: (log.tools?.tool_models?.msrp > 0 ? log.tools.tool_models.msrp : ''),
      is_warranty: false,
      ticket_number: '',
      new_serial_number: '',
      new_model_number: '',
      new_tool_name: '',
    });
    setReceiptFile(null);
    setReceiptPreview(null);
    returnMsg.clearMessage();
    setShowReturn(true);
  }

  function handleReceiptFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setReceiptFile(file);
    setReceiptPreview(URL.createObjectURL(file));
  }

  function clearReceiptPhoto() {
    setReceiptFile(null);
    setReceiptPreview(null);
    if (receiptInputRef.current) receiptInputRef.current.value = '';
  }

  async function markInTransit(log) {
    const oldStatus = log.status;
    await supabase.from('tagout_log').update({ status: 'in_transit' }).eq('id', log.id);
    await supabase.from('tools').update({ status: 'in_transit' }).eq('id', log.tool_id);
    await logHistory(log.tool_id, 'status_change', oldStatus, 'in_transit', profile?.full_name, profile?.id, 'Marked in transit to repair shop');
    load();
  }

  async function markInRepair(log) {
    const oldStatus = log.status;
    await supabase.from('tagout_log').update({ status: 'in_repair' }).eq('id', log.id);
    await supabase.from('tools').update({ status: 'in_repair' }).eq('id', log.tool_id);
    await logHistory(log.tool_id, 'status_change', oldStatus, 'in_repair', profile?.full_name, profile?.id, 'Marked in repair at shop');
    load();
  }

  async function saveReturn() {
    if (!returnForm.resolution_notes?.trim()) { returnMsg.showError('Resolution notes are required.'); return; }

    const isReplaced = returnForm.repair_or_replace === 'replaced';
    const isTraded = returnForm.repair_or_replace === 'traded';

    if (isReplaced || isTraded) {
      if (!returnForm.new_serial_number?.trim()) { returnMsg.showError('New serial number is required when the tool is replaced or traded.'); return; }
      if (!returnForm.new_model_number?.trim()) { returnMsg.showError('New model number is required when the tool is replaced or traded.'); return; }
    }
    if (isTraded && !returnForm.new_tool_name?.trim()) {
      returnMsg.showError('New tool name is required when the tool is traded.'); return;
    }
    if ((isReplaced || isTraded) && !returnForm.replacement_msrp) {
      returnMsg.showError('Replacement MSRP is required when the tool is replaced or traded.'); return;
    }

    setSaving(true);

    let receiptPhotoPath = null;
    if (receiptFile) {
      setReceiptUploading(true);
      const { path, error } = await uploadPhoto(receiptFile, 'receipts');
      setReceiptUploading(false);
      if (error) { returnMsg.showError('Receipt photo upload failed: ' + error); setSaving(false); return; }
      receiptPhotoPath = path;
    }

    const oldStatus = returnLog.status;
    const newStatus = returnForm.new_status;

    await supabase.from('tagout_log').update({
      status: 'Returned',
      return_date: new Date().toISOString(),
      returned_by_name: returnForm.returned_by_name,
      resolution_notes: returnForm.resolution_notes,
      actual_repair_cost: returnForm.is_warranty ? 0 : (returnForm.actual_repair_cost || null),
      repair_or_replace: returnForm.repair_or_replace,
      replacement_msrp: (isReplaced || isTraded) ? (returnForm.replacement_msrp || null) : null,
      is_warranty: returnForm.is_warranty || false,
      ticket_number: returnForm.ticket_number || null,
      ...(receiptPhotoPath && { receipt_photo_url: receiptPhotoPath }),
    }).eq('id', returnLog.id);

    const toolsUpdate = { status: newStatus };
    if (newStatus === 'available') {
      toolsUpdate.is_branch_spare = false;
      toolsUpdate.is_spare = true;
      toolsUpdate.department = null;
      toolsUpdate.station_truck = null;
    }
    if (isReplaced || isTraded) {
      if (returnForm.new_serial_number?.trim()) toolsUpdate.serial_number = returnForm.new_serial_number.trim();
      if (returnForm.new_model_number?.trim()) toolsUpdate.model_number = returnForm.new_model_number.trim();
      toolsUpdate.cost = returnForm.is_warranty ? 0 : (returnForm.actual_repair_cost || null);
      toolsUpdate.tool_model_id = null;
    }
    if (isTraded && returnForm.new_tool_name?.trim()) {
      toolsUpdate.name = returnForm.new_tool_name.trim();
    }
    await supabase.from('tools').update(toolsUpdate).eq('id', returnLog.tool_id);

    const metadataBase = {
      actual_repair_cost: returnForm.actual_repair_cost,
      repair_or_replace: returnForm.repair_or_replace,
    };
    if ((isReplaced || isTraded) && (returnForm.new_serial_number || returnForm.new_model_number)) {
      metadataBase.serial_number_updated = returnForm.new_serial_number?.trim() || null;
      metadataBase.model_number_updated = returnForm.new_model_number?.trim() || null;
    }
    if (isTraded && returnForm.new_tool_name) {
      metadataBase.tool_name_updated = returnForm.new_tool_name.trim();
    }

    const outcomeLabel = isTraded ? 'Traded' : isReplaced ? 'Replaced' : 'Repaired';
    await logHistory(
      returnLog.tool_id, 'status_change', oldStatus, newStatus,
      returnForm.returned_by_name, profile?.id,
      `${outcomeLabel}: ${returnForm.resolution_notes}`,
      metadataBase
    );

    setSaving(false);
    setShowReturn(false);
    load();
  }

  const rf = (k, v) => setReturnForm(p => ({ ...p, [k]: v }));

  function handleMobileTagoutStart() { setShowToolPicker(true); }
  function handleToolSelected(tool) { setSelectedTool(tool); setShowToolPicker(false); setShowMobileTagout(true); }
  function handleMobileTagoutSuccess() { setShowMobileTagout(false); setSelectedTool(null); load(); }

  // Derived outcome flags for JSX
  const isReplaced = returnForm.repair_or_replace === 'replaced';
  const isTraded = returnForm.repair_or_replace === 'traded';
  const showNewToolFields = isReplaced || isTraded;

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div className="page-title">Tag-out log</div>
          <div className="page-sub">Complete record of all tool tag-outs and repairs</div>
        </div>
        {isMobile && (isTeamMember || isManager || isAdmin) && (
          <button className="btn btn-danger" onClick={handleMobileTagoutStart} style={{ flexShrink: 0, marginLeft: '12px' }}>⚠ Tag out</button>
        )}
      </div>

      <div className="stats-grid">
        <div className="stat-card amber"><div className="stat-label">Tagged out</div><div className="stat-value amber">{stats.tagged}</div></div>
        <div className="stat-card red"><div className="stat-label">In repair</div><div className="stat-value red">{stats.repair}</div></div>
        <div className="stat-card blue"><div className="stat-label">In transit</div><div className="stat-value" style={{ color: '#1565C0' }}>{stats.transit}</div></div>
        <div className="stat-card green"><div className="stat-label">Returned</div><div className="stat-value green">{stats.returned}</div></div>
      </div>

      <div className="toolbar">
        <input className="search-input" placeholder="Search tool, reason, tagged by..." value={search} onChange={e => setSearch(e.target.value)} />
        <select className="filter-select" value={filter} onChange={e => setFilter(e.target.value)}>
          <option value="">All records</option>
          <option value="tagged_out">Tagged out</option>
          <option value="in_transit">In transit</option>
          <option value="in_repair">In repair</option>
          <option value="Returned">Returned</option>
        </select>
      </div>

      {loading ? <div className="loading"><div className="spinner" />Loading...</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {filtered.length === 0 && (
            <div className="card">
              <div className="empty-state">
                <div className="empty-state-icon">✅</div>
                <div className="empty-state-title">No records found</div>
                <div className="empty-state-sub">No tag-out records match your filters.</div>
              </div>
            </div>
          )}
          {filtered.map(r => {
            const resolved = r.status === 'Returned';
            return (
              <div key={r.id} className="card" style={{ borderLeft: `4px solid ${resolved ? '#39D353' : r.status === 'in_transit' ? '#1976D2' : '#F5A623'}` }}>
                <div className="card-body">
                  <div className="flex-center gap-2" style={{ marginBottom: '8px', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: 'var(--font-head)', fontSize: '15px', fontWeight: 700 }}>{r.tools?.name || 'Unknown tool'}</div>
                      <div className="text-sm text-muted">{[r.tools?.branches?.name, r.tools?.model_number, r.tools?.serial_number ? 'S/N: ' + r.tools.serial_number : null].filter(Boolean).join(' · ')}</div>
                    </div>
                    <span className={`badge ${STATUS_BADGE[r.status] || 'badge-tagged'}`}>{STATUS_LABEL[r.status] || r.status}</span>
                  </div>

                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '12px', color: 'var(--gray-mid)', marginBottom: '6px' }}>
                    <span>Reason: <strong style={{ color: 'var(--black)' }}>{r.reason}</strong></span>
                    <span>Tagged by: <strong style={{ color: 'var(--black)' }}>{r.tagged_by_name}</strong></span>
                    <span>Date: <strong style={{ color: 'var(--black)' }}>{r.tag_date ? new Date(r.tag_date).toLocaleDateString() : '—'}</strong></span>
                    {r.shop_name && <span>Shop: <strong style={{ color: 'var(--black)' }}>{r.shop_name}</strong></span>}
                    {r.estimated_repair_cost && <span>Est. cost: <strong style={{ color: 'var(--black)' }}>{fmt$(r.estimated_repair_cost)}</strong></span>}
                    {r.actual_repair_cost && <span>Actual cost: <strong style={{ color: 'var(--black)' }}>{fmt$(r.actual_repair_cost)}</strong></span>}
                    {r.return_date && <span>Returned: <strong style={{ color: 'var(--black)' }}>{new Date(r.return_date).toLocaleDateString()}</strong></span>}
                    {r.returned_by_name && <span>By: <strong style={{ color: 'var(--black)' }}>{r.returned_by_name}</strong></span>}
                    {r.ticket_number && <span>Ticket #: <strong style={{ color: 'var(--black)' }}>{r.ticket_number}</strong></span>}
                  </div>

                  {r.issue_description && <div style={{ fontSize: '12px', color: 'var(--gray-mid)', fontStyle: 'italic', marginBottom: '6px' }}>"{r.issue_description}"</div>}
                  {photoUrls[r.id] && (
                    <div style={{ marginBottom: '8px' }}>
                      <img src={photoUrls[r.id]} alt="Damage photo" style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px', objectFit: 'cover', cursor: 'pointer' }} onClick={() => window.open(photoUrls[r.id], '_blank')} />
                    </div>
                  )}
                  {r.resolution_notes && <div style={{ fontSize: '12px', color: 'var(--green-dk)', fontStyle: 'italic', marginBottom: '6px' }}>Resolution: "{r.resolution_notes}"</div>}

                  {!resolved && isManager && (
                    <div className="flex gap-1" style={{ marginTop: '10px', flexWrap: 'wrap' }}>
                      {r.status === 'tagged_out' && <button className="btn btn-ghost btn-sm" onClick={() => markInTransit(r)}>Mark in transit</button>}
                      {(r.status === 'tagged_out' || r.status === 'in_transit') && <button className="btn btn-ghost btn-sm" onClick={() => markInRepair(r)}>Mark in repair</button>}
                      <button className="btn btn-primary btn-sm" onClick={() => openReturn(r)}>Return to service</button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── RETURN TO SERVICE MODAL ── */}
      {showReturn && returnLog && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowReturn(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">Return to service</span>
              <button className="close-btn" onClick={() => setShowReturn(false)}>×</button>
            </div>

            <div style={{ background: '#F0FFF4', padding: '0.75rem 1.25rem', borderBottom: '1px solid #C3E6CB' }}>
              <strong>{returnLog.tools?.name}</strong>
              <div className="text-sm text-muted">{returnLog.reason} · Tagged: {returnLog.tag_date ? new Date(returnLog.tag_date).toLocaleDateString() : '—'}</div>
            </div>

            <div className="modal-body">
              <InlineMessage type={returnMsg.type} message={returnMsg.message} onDismiss={returnMsg.clearMessage} />
              <div className="form-grid">

                <div className="form-group">
                  <label className="form-label">Returned by</label>
                  <input className="form-input" value={returnForm.returned_by_name} onChange={e => rf('returned_by_name', e.target.value)} />
                </div>

                <div className="form-group">
                  <label className="form-label">Shop ticket # <span style={{ fontSize: '11px', color: 'var(--gray-lt)' }}>(optional)</span></label>
                  <input className="form-input" value={returnForm.ticket_number} onChange={e => rf('ticket_number', e.target.value)} placeholder="e.g. INV-2045" />
                </div>

                <div className="form-group">
                  <label className="form-label">Actual cost ($)</label>
                  <input className="form-input" type="number" min="0" step="0.01"
                    value={returnForm.is_warranty ? '0' : returnForm.actual_repair_cost}
                    onChange={e => rf('actual_repair_cost', e.target.value)}
                    disabled={returnForm.is_warranty}
                    style={returnForm.is_warranty ? { background: 'var(--off-white)', color: 'var(--gray-lt)' } : {}}
                  />
                </div>

                <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '2px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
                    <input type="checkbox" checked={returnForm.is_warranty || false} onChange={e => rf('is_warranty', e.target.checked)} />
                    No cost — warranty claim (sets cost to $0.00)
                  </label>
                </div>

                <div className="form-group">
                  <label className="form-label">Outcome</label>
                  <select className="form-select" value={returnForm.repair_or_replace} onChange={e => rf('repair_or_replace', e.target.value)}>
                    <option value="repaired">Repaired</option>
                    <option value="replaced">Replaced — same tool, new unit</option>
                    <option value="traded">Traded / swapped — different tool received</option>
                  </select>
                </div>

                {(isReplaced || isTraded) && (
                  <div className="form-group">
                    <label className="form-label">
                      Replacement MSRP ($)
                      {returnLog.tools?.tool_models?.msrp && !isTraded && (
                        <span style={{ fontSize: '11px', color: 'var(--gray-lt)', marginLeft: '6px' }}>Model MSRP: {fmt$(returnLog.tools.tool_models.msrp)}</span>
                      )}
                      {isTraded && <span style={{ fontSize: '11px', color: 'var(--gray-lt)', marginLeft: '6px' }}>Retail price of the new tool received</span>}
                    </label>
                    <input className="form-input" type="number" min="0" step="0.01" value={returnForm.replacement_msrp} onChange={e => rf('replacement_msrp', e.target.value)} placeholder="What would this cost to buy new?" />
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">New status</label>
                  <select className="form-select" value={returnForm.new_status} onChange={e => rf('new_status', e.target.value)}>
                    <option value="active">Active — return to service</option>
                    <option value="available">Available — return to Dallas pool</option>
                    <option value="retired">Retired — remove from fleet</option>
                  </select>
                </div>

                <div className="form-group full">
                  <label className="form-label">Resolution notes *</label>
                  <textarea className="form-textarea" value={returnForm.resolution_notes} onChange={e => rf('resolution_notes', e.target.value)} placeholder={isTraded ? 'Describe the trade — what was sent in, what was received, and the credit applied...' : 'What was repaired or replaced?'} />
                </div>

                {/* ── NEW TOOL IDENTITY FIELDS (replaced or traded) ── */}
                {showNewToolFields && (
                  <>
                    <div className="form-group full" style={{ marginBottom: '2px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--gray-mid)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px', paddingTop: '4px', borderTop: '1px solid var(--border)' }}>
                        {isTraded ? 'New tool details *' : 'Replacement unit details'} <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>{isTraded ? '(updates tool record)' : '(optional — update tool record)'}</span>
                      </div>
                    </div>

                    {/* Tool name — only shown for traded */}
                    {isTraded && (
                      <div className="form-group full">
                        <label className="form-label">
                          New tool name *
                          <span style={{ fontSize: '11px', color: 'var(--gray-lt)', marginLeft: '6px' }}>Current: {returnLog.tools?.name}</span>
                        </label>
                        <input className="form-input" value={returnForm.new_tool_name} onChange={e => rf('new_tool_name', e.target.value)} placeholder="e.g. Nail Gun" />
                      </div>
                    )}

                    <div className="form-group">
                      <label className="form-label">
                        New serial number *
                        {returnLog.tools?.serial_number && <span style={{ fontSize: '11px', color: 'var(--gray-lt)', marginLeft: '6px' }}>Current: {returnLog.tools.serial_number}</span>}
                      </label>
                      <input className="form-input" value={returnForm.new_serial_number} onChange={e => rf('new_serial_number', e.target.value)} placeholder="Required" />
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        New model number *
                        {returnLog.tools?.model_number && <span style={{ fontSize: '11px', color: 'var(--gray-lt)', marginLeft: '6px' }}>Current: {returnLog.tools.model_number}</span>}
                      </label>
                      <input className="form-input" value={returnForm.new_model_number} onChange={e => rf('new_model_number', e.target.value)} placeholder="Required" />
                    </div>
                  </>
                )}

                {/* ── RECEIPT PHOTO ── */}
                <div className="form-group full" style={{ marginBottom: '2px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--gray-mid)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px', paddingTop: '4px', borderTop: '1px solid var(--border)' }}>
                    Receipt / invoice photo <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span>
                  </div>
                </div>

                <div className="form-group full">
                  {!receiptPreview ? (
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '6px', border: '1px solid var(--border)', cursor: 'pointer', fontSize: '13px', fontWeight: 500, background: 'var(--off-white)', color: 'var(--black)' }}>
                        📷 Take photo
                        <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleReceiptFileChange} />
                      </label>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '6px', border: '1px solid var(--border)', cursor: 'pointer', fontSize: '13px', fontWeight: 500, background: 'var(--off-white)', color: 'var(--black)' }}>
                        📁 Choose file
                        <input ref={receiptInputRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={handleReceiptFileChange} />
                      </label>
                    </div>
                  ) : (
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <img src={receiptPreview} alt="Receipt preview" style={{ maxWidth: '100%', maxHeight: '180px', borderRadius: '8px', objectFit: 'cover', border: '1px solid var(--border)', display: 'block' }} />
                      <button onClick={clearReceiptPhoto} style={{ position: 'absolute', top: '6px', right: '6px', background: 'rgba(0,0,0,0.55)', color: '#fff', border: 'none', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', fontSize: '14px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                      <div style={{ fontSize: '11px', color: 'var(--gray-mid)', marginTop: '4px' }}>{receiptFile?.name}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowReturn(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveReturn} disabled={saving || receiptUploading}>
                {receiptUploading ? 'Uploading photo...' : saving ? 'Saving...' : 'Confirm return'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showToolPicker && (
        <MobileToolPicker profile={profile} isAdmin={isAdmin} onSelect={handleToolSelected} onClose={() => setShowToolPicker(false)} />
      )}

      {showMobileTagout && selectedTool && (
        <MobileTagout tool={selectedTool} onClose={() => { setShowMobileTagout(false); setSelectedTool(null); }} onSuccess={handleMobileTagoutSuccess} />
      )}
    </div>
  );
}